import { useState, useEffect } from 'react';
import api from '../services/api';
import {
  LuPencil, LuTrash2, LuPlus, LuChevronDown, LuChevronUp, LuUsers,
} from 'react-icons/lu';

/* ─────────────────────────────────────────
   Sub-painel: Funcionários de um departamento
───────────────────────────────────────── */
const emptyEmp = { full_name: '', position: '', registration: '', active: true };

function EmployeePanel({ dept }) {
  const [employees,   setEmployees]   = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [form,        setForm]        = useState(emptyEmp);
  const [editingId,   setEditingId]   = useState(null); // null = new
  const [showForm,    setShowForm]    = useState(false);
  const [error,       setError]       = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/departments/${dept.id}/employees`)
      .then((r) => { if (!cancelled) setEmployees(r.data); })
      .catch(console.error)
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dept.id]);

  const openNew = () => { setForm(emptyEmp); setEditingId(null); setShowForm(true); setError(''); };

  const openEdit = (emp) => {
    setForm({ full_name: emp.full_name, position: emp.position || '', registration: emp.registration || '', active: emp.active });
    setEditingId(emp.id);
    setShowForm(true);
    setError('');
  };

  const handleSave = async () => {
    if (!form.full_name.trim()) { setError('Nome é obrigatório'); return; }
    try {
      if (editingId) {
        const { data } = await api.put(`/employees/${editingId}`, form);
        setEmployees((p) => p.map((e) => (e.id === editingId ? { ...e, ...data } : e)));
      } else {
        // dept já pertence a uma única empresa — mesmo motivo do fix em
        // EmployeesPage.js: sem isso, superadmin/admin de matriz recebe
        // "Selecione a empresa..." sem ter como resolver aqui.
        const { data } = await api.post('/employees', { ...form, department_id: dept.id, company_id: dept.company_id });
        setEmployees((p) => [...p, data].sort((a, b) => a.full_name.localeCompare(b.full_name)));
      }
      setShowForm(false);
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Erro ao salvar');
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remover "${name}"?`)) return;
    try {
      await api.delete(`/employees/${id}`);
      setEmployees((p) => p.filter((e) => e.id !== id));
    } catch (e) {
      setError(e.response?.data?.error || 'Erro ao remover');
    }
  };

  return (
    <div style={{ borderTop: '1px solid var(--border)', padding: '14px 20px 16px', background: 'var(--surface-2)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
          <LuUsers style={{ marginRight: 5 }} />Funcionários ({employees.length})
        </span>
        <button onClick={openNew} style={smBtn('var(--text-1)')}>
          <LuPlus size={11} style={{ marginRight: 4 }} /> Novo
        </button>
      </div>

      {error && <div className="alert-custom danger" style={{ marginBottom: 8, fontSize: 12 }}>{error}</div>}

      {showForm && (
        <div style={{ background: 'var(--surface-1)', border: '1px solid var(--border)', borderRadius: 8, padding: 14, marginBottom: 12 }}>
          <div className="resp-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 8, alignItems: 'end' }}>
            <div>
              <label style={lbl}>Nome *</label>
              <input className="form-control" style={{ fontSize: 13, padding: '6px 10px' }}
                value={form.full_name} onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
                placeholder="Nome completo" autoFocus />
            </div>
            <div>
              <label style={lbl}>Cargo</label>
              <input className="form-control" style={{ fontSize: 13, padding: '6px 10px' }}
                value={form.position} onChange={(e) => setForm((f) => ({ ...f, position: e.target.value }))}
                placeholder="Ex: Operador" />
            </div>
            <div>
              <label style={lbl}>Matrícula</label>
              <input className="form-control" style={{ fontSize: 13, padding: '6px 10px' }}
                value={form.registration} onChange={(e) => setForm((f) => ({ ...f, registration: e.target.value }))}
                placeholder="Ex: 001" />
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn-salvar" style={{ fontSize: 12, padding: '6px 14px' }} onClick={handleSave}>
                Salvar
              </button>
              <button onClick={() => setShowForm(false)} style={{ ...smBtn('var(--text-3)'), padding: '6px 10px' }}>
                ✕
              </button>
            </div>
          </div>
        </div>
      )}

      {loading && <div style={{ fontSize: 12, color: 'var(--text-3)', padding: '6px 0' }}>Carregando…</div>}

      {!loading && employees.length === 0 && !showForm && (
        <div style={{ fontSize: 12, color: 'var(--text-3)', fontStyle: 'italic', padding: '4px 0' }}>
          Nenhum funcionário cadastrado
        </div>
      )}

      {employees.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              {['Nome', 'Cargo', 'Matrícula', 'Status', ''].map((h) => (
                <th key={h} style={{ padding: '5px 8px', textAlign: 'left', color: 'var(--text-3)', fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {employees.map((emp) => (
              <tr key={emp.id} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '7px 8px', fontWeight: 600, color: 'var(--text-1)' }}>{emp.full_name}</td>
                <td style={{ padding: '7px 8px', color: 'var(--text-2)' }}>{emp.position || '—'}</td>
                <td style={{ padding: '7px 8px', color: 'var(--text-2)' }}>{emp.registration || '—'}</td>
                <td style={{ padding: '7px 8px' }}>
                  <span style={{
                    padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                    background: emp.active ? 'var(--success-soft)' : 'var(--surface-2)',
                    color: emp.active ? 'var(--success)' : 'var(--text-3)',
                  }}>
                    {emp.active ? 'Ativo' : 'Inativo'}
                  </span>
                </td>
                <td style={{ padding: '7px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button onClick={() => openEdit(emp)} style={iconBtn('var(--text-1)')} title="Editar"><LuPencil size={12} /></button>
                  <button onClick={() => handleDelete(emp.id, emp.full_name)} style={iconBtn('var(--danger)')} title="Remover"><LuTrash2 size={12} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────
   Página principal de Departamentos
───────────────────────────────────────── */
const emptyDept = { name: '', description: '', company_id: '' };

export default function DepartmentsPage() {
  const [departments, setDepartments] = useState([]);
  const [companies,   setCompanies]   = useState([]);
  const [form,          setForm]          = useState(emptyDept);
  const [editingId,     setEditingId]     = useState(null);
  // Empresa já definida no momento em que o form abriu — trocar depois de
  // criado não é suportado (efeito cascata em funcionário/indicador já
  // vinculado), então o campo fica travado nesse caso. Só fica editável
  // quando o departamento está órfão (reparo de dado antigo).
  const [companyLocked, setCompanyLocked] = useState(false);
  const [showForm,    setShowForm]    = useState(false);
  const [expanded,    setExpanded]    = useState({});
  const [error,       setError]       = useState('');
  const [success,     setSuccess]     = useState('');

  // Mais de uma empresa no sistema = precisa perguntar explicitamente qual;
  // com só 1, nem mostra o campo (o backend resolve sozinho nesse caso).
  const showCompanyField = companies.length > 1;

  useEffect(() => {
    api.get('/departments').then((r) => setDepartments(r.data)).catch(console.error);
    // Sempre o conjunto COMPLETO de empresas, ignorando a "empresa ativa" do
    // CompanySwitcher — este campo precisa listar todas as opções possíveis.
    api.get('/companies', { headers: { 'X-Active-Company-Id': '' } })
      .then((r) => setCompanies(r.data)).catch(console.error);
  }, []);

  const toggleExpand = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  const openNew = () => {
    setForm(emptyDept);
    setEditingId(null);
    setCompanyLocked(false);
    setShowForm(true);
    setError('');
  };

  const openEdit = (dept) => {
    setForm({
      name: dept.name,
      description: dept.description || '',
      company_id: dept.company_id != null ? String(dept.company_id) : '',
    });
    setEditingId(dept.id);
    setCompanyLocked(dept.company_id != null);
    setShowForm(true);
    setError('');
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Nome é obrigatório'); return; }
    if (showCompanyField && !form.company_id) { setError('Empresa é obrigatória'); return; }
    try {
      if (editingId) {
        const { data } = await api.put(`/departments/${editingId}`, form);
        setDepartments((p) => p.map((d) => (d.id === editingId ? { ...d, ...data } : d)));
        setSuccess('Departamento atualizado');
      } else {
        const { data } = await api.post('/departments', form);
        setDepartments((p) => [...p, data]);
        setSuccess('Departamento criado');
      }
      setShowForm(false);
      setError('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.response?.data?.error || 'Erro ao salvar');
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Excluir departamento "${name}"?`)) return;
    try {
      await api.delete(`/departments/${id}`);
      setDepartments((p) => p.filter((d) => d.id !== id));
    } catch (e) {
      setError(e.response?.data?.error || 'Erro ao excluir');
    }
  };

  return (
    <div className="page-card">
      <div className="page-card-header">
        <div>
          <div className="page-card-title">Departamentos</div>
          <div className="page-card-subtitle">Gerencie departamentos e seus funcionários</div>
        </div>
        <button className="btn-salvar" onClick={openNew}>
          <LuPlus style={{ marginRight: 6 }} /> Novo Departamento
        </button>
      </div>

      {error   && <div className="alert-custom danger"  style={{ marginBottom: 12 }}>{error}</div>}
      {success && <div className="alert-custom success" style={{ marginBottom: 12 }}>{success}</div>}

      {showForm && (
        <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 10, padding: 18, marginBottom: 20 }}>
          <div className="form-section-title">{editingId ? 'Editar Departamento' : 'Novo Departamento'}</div>
          <div className="resp-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12, marginTop: 12 }}>
            <div>
              <label className="form-label">Nome *</label>
              <input className="form-control" value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Ex: Cobrança" autoFocus />
            </div>
            <div>
              <label className="form-label">Descrição</label>
              <input className="form-control" value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Descrição opcional" />
            </div>
          </div>

          {showCompanyField && (
            <div style={{ marginTop: 12 }}>
              <label className="form-label">Empresa *</label>
              <select className="form-control" value={form.company_id} disabled={companyLocked}
                onChange={(e) => setForm((f) => ({ ...f, company_id: e.target.value }))}>
                <option value="">Selecione…</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.trade_name || c.name}</option>
                ))}
              </select>
              {companyLocked && (
                <small style={{ fontSize: 11, color: 'var(--text-3)' }}>
                  Empresa já definida para este departamento — não pode ser trocada depois de criado.
                </small>
              )}
            </div>
          )}

          <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
            <button className="btn-salvar" onClick={handleSave}>Salvar</button>
            <button onClick={() => setShowForm(false)} style={smBtn('var(--text-3)')}>Cancelar</button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {departments.length === 0 && (
          <div className="empty-state"><p>Nenhum departamento cadastrado</p></div>
        )}
        {departments.map((dept) => (
          <div key={dept.id} style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', background: 'var(--surface-1)' }}>
            {/* Cabeçalho do departamento */}
            <div style={{ display: 'flex', alignItems: 'center', padding: '14px 20px', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-1)' }}>{dept.name}</div>
                {dept.description && <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>{dept.description}</div>}
              </div>
              <button onClick={() => openEdit(dept)} style={iconBtn('var(--text-1)')} title="Editar"><LuPencil size={14} /></button>
              <button onClick={() => handleDelete(dept.id, dept.name)} style={iconBtn('var(--danger)')} title="Excluir"><LuTrash2 size={14} /></button>
              <button
                onClick={() => toggleExpand(dept.id)}
                style={{ ...iconBtn('var(--text-1)'), borderLeft: '1px solid var(--border)', paddingLeft: 12, marginLeft: 4 }}
                title={expanded[dept.id] ? 'Recolher' : 'Ver funcionários'}
              >
                {expanded[dept.id] ? <LuChevronUp size={14} /> : <LuChevronDown size={14} />}
              </button>
            </div>

            {/* Painel de funcionários (expansível) */}
            {expanded[dept.id] && <EmployeePanel dept={dept} />}
          </div>
        ))}
      </div>
    </div>
  );
}

const lbl   = { fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', display: 'block', marginBottom: 4 };
const smBtn = (color) => ({ background: 'none', border: `1px solid ${color}`, borderRadius: 6, padding: '5px 12px', cursor: 'pointer', fontSize: 12, color, fontFamily: 'inherit' });
const iconBtn = (color) => ({ background: 'none', border: 'none', cursor: 'pointer', color, padding: '4px 6px', borderRadius: 5, fontSize: 14 });
